import LogService from "../LogService";
import AudioDevice from "./AudioDevice";
import RecorderRTC from "recordrtc";
import { showErrorModal } from "../../components/Modals/InformationModalReducer";

class RecorderService {
  recorder?: RecorderRTC = undefined;
  dateStarted = 0;
  recordingTimer: ReturnType<typeof setInterval> | undefined;
  duration: number = 45 * 1000;
  onData?: (data: Blob, duration: number) => void = undefined;
  onError?: (error: string) => void = undefined;
  initialized = false;

  _mediaStream?: MediaStream = undefined;

  initialize = (
    duration: number,
    audioDeviceId: string | undefined,
    onData: (data: Blob, duration: number) => void,
    onError: (error: string) => void,
  ) => {
    this.onData = onData;
    this.onError = onError;
    this.duration = duration;
    this.initialized = true;

    navigator.mediaDevices
      .getUserMedia({
        audio:
          audioDeviceId === undefined
            ? true
            : {
                deviceId: audioDeviceId,
              },
      })
      .then(stream => {
        this._mediaStream = stream;

        if (!stream) {
          showErrorModal(
            "Recording Failed",
            "Could not access your microphone.  Make sure you have allowed access to your microphone.",
          );
          LogService.exception("Media stream is null", `RecorderService.initialize`);
          onError("No Media Stream Found");
        }
      })
      .catch(error => {
        showErrorModal(
          "Recording Failed",
          "Could not access your microphone.  Make sure you have allowed access to your microphone.",
        );
        LogService.exception(error, `RecorderService.initialize`, "Failed to get user media.");
        onError(error.name);
      });
  };

  cleanupAudio = () => {
    // Stop the microphone access
    if (this._mediaStream) {
      this._mediaStream.getTracks().forEach(track => track.stop());
      this._mediaStream = undefined;
    }

    this.onError = undefined;
    this.onData = undefined;
  };

  startRecording = () => {
    if (this._mediaStream) {
      this.recorder = new RecorderRTC(this._mediaStream, {
        recorderType: RecorderRTC.StereoAudioRecorder,
        type: "audio",
        mimeType: "audio/webm",
        numberOfAudioChannels: 1,
        sampleRate: 48000,
        disableLogs: true,
      });

      this.dateStarted = new Date().getTime();
      this.recorder.startRecording();
      this.recordingTimer = setInterval(async () => {
        await this._processAudio(true);
      }, this.duration);
    } else {
      LogService.exception(
        new Error(),
        `RecorderService.startRecording`,
        "Tried to start a recording without the media stream.",
      );
      if (this.onError) {
        this.onError("Failed to start recording");
      }
    }
  };

  _processAudio = async (continueRecording: boolean) => {
    return new Promise<void>(resolve => {
      if (this.recorder) {
        const newDuration = new Date().getTime();
        const blobDuration = newDuration - this.dateStarted;
        this.dateStarted = newDuration;

        this.recorder.stopRecording(async () => {
          if (this.recorder) {
            const blob = this.recorder.getBlob();

            if (continueRecording) {
              this.recorder.reset();
              this.recorder.startRecording();
            }

            // Send the blob to be processed
            if (this.onData) {
              await this.onData(blob, blobDuration);
            }
          }

          resolve();
        });
      }
    });
  };

  stopRecording = async () => {
    if (this.recordingTimer) {
      clearInterval(this.recordingTimer);
      this.recordingTimer = undefined;
    }

    if (this.recorder) {
      await this._processAudio(false);
    }
  };

  static listAudioDevices = async (): Promise<AudioDevice[]> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const devices = await navigator.mediaDevices.enumerateDevices();
      stream.getTracks().forEach(track => track.stop());

      return devices
        .filter(obj => obj.kind === "audioinput" && obj.deviceId !== "communications")
        .map(obj => {
          const device: AudioDevice = {
            deviceId: obj.deviceId,
            label: obj.label,
            isSystemDefault: false,
          };

          if (obj.deviceId === "default") {
            device.isSystemDefault = true;
            device.label = "Same as System";
          }

          return device;
        });
    } catch (e) {
      LogService.exception(e, `RecorderService.listAudioDevices`, "Failed to retrieve audio devices");
      throw e;
    }
  };
}

export default RecorderService;
